// src/config/thresholds.js
// Configuration file for health parameter thresholds
// This file defines the normal, warning, and dangerous ranges for various health metrics

export const HEALTH_THRESHOLDS = {
  // Blood Pressure thresholds (mmHg)
  bloodPressure: {
    systolic: {
      normal: { min: 90, max: 120.99 },
      warning: { min: 121, max: 139.99 },
      dangerous: { min: 140, max: 999 }
    },
    diastolic: {
      normal: { min: 60, max: 80.99 },
      warning: { min: 81, max: 89.99 },
      dangerous: { min: 90, max: 999 }
    }
  },

  // Heart Rate thresholds (bpm)
  heartRate: {
    normal: { min: 60, max: 100 },
    warning: { min: 50, max: 110 }, // Extended range for warning
    dangerous: [
      { min: 0, max: 49 },    // Too low
      { min: 111, max: 999 }  // Too high
    ]
  },

  // BMI thresholds
  bmi: {
    normal: { min: 18.5, max: 24.9 },
    warning: [
      { min: 17, max: 18.4 },   // Underweight warning
      { min: 25, max: 29.9 }    // Overweight warning
    ],
    dangerous: [
      { min: 0, max: 16.9 },    // Severely underweight
      { min: 30, max: 999 }     // Obese
    ]
  },

  // Cardiovascular Risk Factor (percentage)
  cardiovascularRisk: {
    normal: { min: 0, max: 10 },
    warning: { min: 10.1, max: 20 },
    dangerous: { min: 20.1, max: 100 }
  },

  // Benedict Value (kcal/day) - varies by age and gender
  benedictValue: {
    male: {
      normal: { min: 1500, max: 2500 },
      warning: [
        { min: 1200, max: 1499 },
        { min: 2501, max: 3000 }
      ],
      dangerous: [
        { min: 0, max: 1199 },
        { min: 3001, max: 9999 }
      ]
    },
    female: {
      normal: { min: 1200, max: 2000 },
      warning: [
        { min: 1000, max: 1199 },
        { min: 2001, max: 2500 }
      ],
      dangerous: [
        { min: 0, max: 999 },
        { min: 2501, max: 9999 }
      ]
    }
  },

  // PAL Value (Physical Activity Level)
  palValue: {
    normal: { min: 1.4, max: 2.4 },
    warning: [
      { min: 1.2, max: 1.39 },  // Sedentary warning
      { min: 2.41, max: 2.8 }   // Very active warning
    ],
    dangerous: [
      { min: 0, max: 1.19 },    // Extremely sedentary
      { min: 2.81, max: 999 }   // Excessive activity
    ]
  },

  // TEE Value (Total Energy Expenditure)
  teeValue: {
    male: {
      normal: { min: 2000, max: 3500 },
      warning: [
        { min: 1600, max: 1999 },
        { min: 3501, max: 4000 }
      ],
      dangerous: [
        { min: 0, max: 1599 },
        { min: 4001, max: 9999 }
      ]
    },
    female: {
      normal: { min: 1600, max: 2800 },
      warning: [
        { min: 1300, max: 1599 },
        { min: 2801, max: 3200 }
      ],
      dangerous: [
        { min: 0, max: 1299 },
        { min: 3201, max: 9999 }
      ]
    }
  },

  // Therapy Adherence (percentage)
  therapyAdherence: {
    normal: { min: 80, max: 100 },
    warning: { min: 60, max: 79 },
    dangerous: { min: 0, max: 59 }
  },

  // Questionnaire Scores
  fagerstromScore: {
    normal: { min: 0, max: 3 },      // Low dependence
    warning: { min: 4, max: 6 },     // Moderate dependence
    dangerous: { min: 7, max: 10 }   // High dependence
  },

  stressScore: {
    normal: { min: 0, max: 13 },     // Low stress
    warning: { min: 14, max: 26 },   // Moderate stress
    dangerous: { min: 27, max: 40 }  // High stress
  },

  // New questionnaire thresholds (adjust ranges as needed)
  hypertension_awareness_score: {
    normal: { min: 67, max: 100 },
    warning: { min: 34, max: 66 },
    dangerous: { min: 0, max: 33 }
  },
  blood_pressure_monitoring_score: {
    normal: { min: 67, max: 100 },
    warning: { min: 34, max: 66 },
    dangerous: { min: 0, max: 33 }
  },
  therapy_adherence_score: {
    normal: { min: 67, max: 100 },
    warning: { min: 34, max: 66 },
    dangerous: { min: 0, max: 33 }
  },
  diet_score: {
    normal: { min: 67, max: 100 },
    warning: { min: 34, max: 66 },
    dangerous: { min: 0, max: 33 }
  },
  physical_activity_score: {
    normal: { min: 67, max: 100 },
    warning: { min: 34, max: 66 },
    dangerous: { min: 0, max: 33 }
  },
  sleep_score: {
    normal: { min: 67, max: 100 },
    warning: { min: 34, max: 66 },
    dangerous: { min: 0, max: 33 }
  }
};

// Color constants
export const HEALTH_COLORS = {
  normal: '#22c55e',    // Green
  warning: '#eab308',   // Yellow/Amber
  dangerous: '#ef4444', // Red
  neutral: '#6b7280'    // Gray for N/A values
};

// Health metric tags for unique identification
export const HEALTH_METRIC_TAGS = {
  // Blood Pressure metrics
  SYSTOLIC_BLOOD_PRESSURE: 'systolic_blood_pressure',
  DIASTOLIC_BLOOD_PRESSURE: 'diastolic_blood_pressure',
  AVERAGE_SYSTOLIC_PRESSURE: 'average_systolic_pressure',
  AVERAGE_DIASTOLIC_PRESSURE: 'average_diastolic_pressure',
  MAX_SYSTOLIC_PRESSURE: 'max_systolic_pressure',
  MIN_SYSTOLIC_PRESSURE: 'min_systolic_pressure',
  MAX_DIASTOLIC_PRESSURE: 'max_diastolic_pressure',
  MIN_DIASTOLIC_PRESSURE: 'min_diastolic_pressure',
  NUMBER_SYSTOLIC_MEASUREMENTS: 'number_systolic_measurements',
  NUMBER_DIASTOLIC_MEASUREMENTS: 'number_diastolic_measurements',
  
  // Heart Rate metrics
  HEART_RATE: 'heart_rate',
  AVERAGE_HEART_RATE: 'average_heart_rate',
  MAX_HEART_RATE: 'max_heart_rate',
  MIN_HEART_RATE: 'min_heart_rate',
  NUMBER_HEART_RATE_MEASUREMENTS: 'number_heart_rate_measurements',
  
  // Body metrics
  BMI_VALUE: 'bmi_value',
  BENEDICT_VALUE: 'benedict_value',
  PAL_VALUE: 'pal_value',
  TEE_VALUE: 'tee_value',
  
  // Risk and health metrics
  CARDIOVASCULAR_RISK: 'cardiovascular_risk',
  THERAPY_ADHERENCE: 'therapy_adherence',
  RESPECTED_ASSUMPTIONS: 'respected_assumptions',
  
  // Questionnaire scores
  FAGERSTROM_SCORE: 'fagerstrom_score',
  STRESS_SCORE: 'stress_score',
  HYPERTENSION_AWARENESS_SCORE: 'hypertension_awareness_score',
  BLOOD_PRESSURE_MONITORING_SCORE: 'blood_pressure_monitoring_score',
  THERAPY_ADHERENCE_SCORE: 'therapy_adherence_score',
  DIET_SCORE: 'diet_score',
  PHYSICAL_ACTIVITY_SCORE: 'physical_activity_score',
  SLEEP_SCORE: 'sleep_score',
  
  // Educational and notification metrics
  EDUCATIONAL_NOTIFICATIONS: 'educational_notifications',
  TOTAL_ASSUMPTION_NOTIFICATIONS: 'total_assumption_notifications',
  
  // Generic metrics (for custom use cases)
  GENERIC_HEALTH_METRIC: 'generic_health_metric'
};

// Map health metric tags to their corresponding thresholds
export const METRIC_TAG_TO_THRESHOLD = {
  [HEALTH_METRIC_TAGS.SYSTOLIC_BLOOD_PRESSURE]: 'bloodPressure.systolic',
  [HEALTH_METRIC_TAGS.DIASTOLIC_BLOOD_PRESSURE]: 'bloodPressure.diastolic',
  [HEALTH_METRIC_TAGS.AVERAGE_SYSTOLIC_PRESSURE]: 'bloodPressure.systolic',
  [HEALTH_METRIC_TAGS.AVERAGE_DIASTOLIC_PRESSURE]: 'bloodPressure.diastolic',
  [HEALTH_METRIC_TAGS.MAX_SYSTOLIC_PRESSURE]: 'bloodPressure.systolic',
  [HEALTH_METRIC_TAGS.MIN_SYSTOLIC_PRESSURE]: 'bloodPressure.systolic',
  [HEALTH_METRIC_TAGS.MAX_DIASTOLIC_PRESSURE]: 'bloodPressure.diastolic',
  [HEALTH_METRIC_TAGS.MIN_DIASTOLIC_PRESSURE]: 'bloodPressure.diastolic',
  [HEALTH_METRIC_TAGS.HEART_RATE]: 'heartRate',
  [HEALTH_METRIC_TAGS.AVERAGE_HEART_RATE]: 'heartRate',
  [HEALTH_METRIC_TAGS.MAX_HEART_RATE]: 'heartRate',
  [HEALTH_METRIC_TAGS.MIN_HEART_RATE]: 'heartRate',
  [HEALTH_METRIC_TAGS.NUMBER_HEART_RATE_MEASUREMENTS]: 'heartRate',
  [HEALTH_METRIC_TAGS.BMI_VALUE]: 'bmi',
  [HEALTH_METRIC_TAGS.BENEDICT_VALUE]: 'benedictValue',
  [HEALTH_METRIC_TAGS.PAL_VALUE]: 'palValue',
  [HEALTH_METRIC_TAGS.TEE_VALUE]: 'teeValue',
  [HEALTH_METRIC_TAGS.CARDIOVASCULAR_RISK]: 'cardiovascularRisk',
  [HEALTH_METRIC_TAGS.THERAPY_ADHERENCE]: 'therapyAdherence',
  [HEALTH_METRIC_TAGS.FAGERSTROM_SCORE]: 'fagerstromScore',
  [HEALTH_METRIC_TAGS.STRESS_SCORE]: 'stressScore'
};

export const getHealthStatusByTag = (value, metricTag, gender = null) => {
  // console.log('getHealthStatusByTag called with:', { value, metricTag, gender });
  // Handle N/A or invalid values
  if (value === 'N/A' || value === null || value === undefined || value === '') {
    return 'neutral';
  }
  // console.log('Parsed value:', value);

  const numValue = parseFloat(typeof value === 'string' ? value.replace(/[^\d.-]/g, '') : value);
  if (isNaN(numValue)) {
    return 'neutral';
  }
  // console.log('Numeric value:', numValue);

  // Get threshold path from metric tag
  const thresholdPath = METRIC_TAG_TO_THRESHOLD[metricTag];
  if (!thresholdPath) {
    return 'neutral';
  }
  // console.log('Threshold path:', thresholdPath);

  // Navigate to the correct threshold object
  let thresholds = HEALTH_THRESHOLDS;
  const keys = thresholdPath.split('.');
  for (const key of keys) {
    thresholds = thresholds[key];
    if (!thresholds) return 'neutral';
  }

  // console.log('Thresholds:', thresholds);

  const healthStatus = getHealthStatus(numValue, thresholds, gender);
  // console.log('Determined health status:', healthStatus);
  return healthStatus;
};

/**
 * Determines the health status based on value and thresholds
 * @param {number|string} value - The value to evaluate
 * @param {object} thresholds - The threshold configuration for this metric
 * @param {string} gender - Optional gender for gender-specific thresholds
 * @returns {string} - 'normal', 'warning', 'dangerous', or 'neutral'
 */
export const getHealthStatus = (value, thresholds, gender = null) => {
  // Handle N/A or invalid values
  if (value === 'N/A' || value === null || value === undefined || value === '') {
    return 'neutral';
  }

  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return 'neutral';
  }

  // Handle gender-specific thresholds
  const threshold = gender && thresholds[gender] ? thresholds[gender] : thresholds;

  // console.log('Evaluating health status for value:', numValue, 'with thresholds:', threshold);

  // Check dangerous ranges first
  if (threshold.dangerous) {
    if (Array.isArray(threshold.dangerous)) {
      for (const range of threshold.dangerous) {
        if (numValue >= range.min && numValue < range.max + 0.001) {
          return 'dangerous';
        }
      }
    } else {
      if (numValue >= threshold.dangerous.min && numValue < threshold.dangerous.max + 0.001) {
        return 'dangerous';
      }
    }
  }

  // Check warning ranges
  if (threshold.warning) {
    if (Array.isArray(threshold.warning)) {
      for (const range of threshold.warning) {
        if (numValue >= range.min && numValue < range.max + 0.001) {
          return 'warning';
        }
      }
    } else {
      if (numValue >= threshold.warning.min && numValue < threshold.warning.max + 0.001) {
        return 'warning';
      }
    }
  }

  // Check normal range
  if (threshold.normal) {
    if (numValue >= threshold.normal.min && numValue < threshold.normal.max + 0.001) {
      return 'normal';
    }
  }

  // If no range matches, default to neutral
  return 'neutral';
};

/**
 * Get the appropriate color for a health status
 * @param {string} status - 'normal', 'warning', 'dangerous', or 'neutral'
 * @returns {string} - Hex color code
 */
export const getHealthColor = (status) => {
  return HEALTH_COLORS[status] || HEALTH_COLORS.neutral;
};

// Blood Pressure Classification (American Heart Association Guidelines)
export const BLOOD_PRESSURE_THRESHOLDS = {
  systolic: {
    normal: { max: 120 },
    elevated: { min: 120, max: 129 },
    stage1_hypertension: { min: 130, max: 139 },
    stage2_hypertension: { min: 140, max: 179 },
    hypertensive_crisis: { min: 180 }
  },
  diastolic: {
    normal: { max: 80 },
    elevated: { max: 80 }, // Same as normal for elevated
    stage1_hypertension: { min: 80, max: 89 },
    stage2_hypertension: { min: 90, max: 119 },
    hypertensive_crisis: { min: 120 }
  }
};

export const BLOOD_PRESSURE_COLOR_THRESHOLDS = {
  systolic: {
    normal: 120,    // ≤120 = Green (normale)
    warning: 130,   // 121-130 = Yellow (attenzione)  
    dangerous: 131  // ≥131 = Red (critico)
  },
  diastolic: {
    normal: 80,     // ≤80 = Green (normale)
    warning: 90,    // 81-90 = Yellow (attenzione)
    dangerous: 91   // ≥91 = Red (critico)
  }
};

/**
 * Get blood pressure color based on value and type
 * @param {number} value - Blood pressure value
 * @param {boolean} isSystolic - True for systolic (max), false for diastolic (min)
 * @returns {string} - Color hex code
 */
export const getBloodPressureColor = (value, isSystolic = true) => {
  const thresholds = isSystolic 
    ? BLOOD_PRESSURE_COLOR_THRESHOLDS.systolic 
    : BLOOD_PRESSURE_COLOR_THRESHOLDS.diastolic;

  if (value <= thresholds.normal) {
    return '#22c55e'; // Green - Normal
  } else if (value <= thresholds.warning) {
    return '#eab308'; // Yellow - Warning
  } else {
    return '#ef4444'; // Red - Dangerous
  }
};

/**
 * Get blood pressure status text
 * @param {number} value - Blood pressure value
 * @param {boolean} isSystolic - True for systolic (max), false for diastolic (min)
 * @returns {string} - Status text
 */
export const getBloodPressureStatus = (value, isSystolic = true) => {
  const thresholds = isSystolic 
    ? BLOOD_PRESSURE_COLOR_THRESHOLDS.systolic 
    : BLOOD_PRESSURE_COLOR_THRESHOLDS.diastolic;

  if (value <= thresholds.normal) {
    return 'normal';
  } else if (value <= thresholds.warning) {
    return 'warning';
  } else {
    return 'dangerous';
  }
};

/**
 * Get detailed blood pressure classification
 * @param {number} systolic - Systolic pressure value
 * @param {number} diastolic - Diastolic pressure value
 * @returns {object} - Classification details
 */
export const getBloodPressureClassification = (systolic, diastolic) => {
  // Determine classification based on the higher category of either systolic or diastolic
  let classification = 'normal';
  let color = '#22c55e';
  let priority = 0;

  // Check systolic
  if (systolic >= 180) {
    classification = 'hypertensive_crisis';
    color = '#dc2626'; // Dark red
    priority = 5;
  } else if (systolic >= 140) {
    classification = 'stage2_hypertension';
    color = '#ef4444'; // Red
    priority = 4;
  } else if (systolic >= 130) {
    classification = 'stage1_hypertension';
    color = '#f59e0b'; // Orange
    priority = 3;
  } else if (systolic >= 120) {
    classification = 'elevated';
    color = '#eab308'; // Yellow
    priority = 2;
  } else {
    classification = 'normal';
    color = '#22c55e'; // Green
    priority = 1;
  }

  // Check diastolic and use higher priority classification
  let diastolicClassification = 'normal';
  let diastolicPriority = 1;

  if (diastolic >= 120) {
    diastolicClassification = 'hypertensive_crisis';
    diastolicPriority = 5;
  } else if (diastolic >= 90) {
    diastolicClassification = 'stage2_hypertension';
    diastolicPriority = 4;
  } else if (diastolic >= 80) {
    diastolicClassification = 'stage1_hypertension';
    diastolicPriority = 3;
  }

  // Use the higher priority classification
  if (diastolicPriority > priority) {
    classification = diastolicClassification;
    switch(classification) {
      case 'hypertensive_crisis':
        color = '#dc2626';
        break;
      case 'stage2_hypertension':
        color = '#ef4444';
        break;
      case 'stage1_hypertension':
        color = '#f59e0b';
        break;
    }
  }

  return {
    classification,
    color,
    systolicStatus: getBloodPressureStatus(systolic, true),
    diastolicStatus: getBloodPressureStatus(diastolic, false)
  };
};

// Blood pressure threshold validation limits
export const BLOOD_PRESSURE_VALIDATION = {
  // Minimum acceptable values (below these would be concerning)
  MIN_SYSTOLIC: 70,    // Below 70 mmHg systolic is severely hypotensive
  MIN_DIASTOLIC: 40,   // Below 40 mmHg diastolic is severely hypotensive
  
  // Maximum acceptable values (above these would be extremely high)
  MAX_SYSTOLIC: 250,   // Above 250 mmHg systolic is extremely dangerous
  MAX_DIASTOLIC: 150,  // Above 150 mmHg diastolic is extremely dangerous
  
  // Minimum difference between thresholds
  MIN_THRESHOLD_DIFFERENCE: 10, // At least 10 mmHg difference between min and max thresholds
  
  // Recommended ranges for setting thresholds
  RECOMMENDED: {
    SYSTOLIC: {
      MIN_THRESHOLD_RANGE: { min: 80, max: 140 },    // Recommended range for setting min threshold
      MAX_THRESHOLD_RANGE: { min: 120, max: 180 }    // Recommended range for setting max threshold
    },
    DIASTOLIC: {
      MIN_THRESHOLD_RANGE: { min: 50, max: 90 },     // Recommended range for setting min threshold  
      MAX_THRESHOLD_RANGE: { min: 80, max: 110 }     // Recommended range for setting max threshold
    }
  }
};

/**
 * Validates a single blood pressure threshold value
 * @param {number} threshold - Threshold value to validate
 * @param {boolean} isSystolic - True for systolic, false for diastolic
 * @returns {object} - Validation result with isValid boolean and error message
 */
export const validateSingleBloodPressureThreshold = (threshold, isSystolic = true) => {
  const value = parseFloat(threshold);
  
  // Check if value is a valid number
  if (isNaN(value)) {
    return {
      isValid: false,
      errorKey: 'invalid_threshold_values',
      errorParams: {}
    };
  }
  
  const limits = isSystolic ? 
    { 
      minLimit: BLOOD_PRESSURE_VALIDATION.MIN_SYSTOLIC, 
      maxLimit: BLOOD_PRESSURE_VALIDATION.MAX_SYSTOLIC 
    } : 
    { 
      minLimit: BLOOD_PRESSURE_VALIDATION.MIN_DIASTOLIC, 
      maxLimit: BLOOD_PRESSURE_VALIDATION.MAX_DIASTOLIC 
    };
  
  // Check if value is within acceptable range
  if (value < limits.minLimit) {
    return {
      isValid: false,
      errorKey: 'min_threshold_too_low',
      errorParams: { min: limits.minLimit }
    };
  }
  
  if (value > limits.maxLimit) {
    return {
      isValid: false,
      errorKey: 'max_threshold_too_high',
      errorParams: { max: limits.maxLimit }
    };
  }
  
  return {
    isValid: true,
    errorKey: null,
    errorParams: {}
  };
};

/**
 * Validates blood pressure threshold values
 * @param {number} minThreshold - Minimum threshold value
 * @param {number} maxThreshold - Maximum threshold value
 * @param {boolean} isSystolic - True for systolic, false for diastolic
 * @returns {object} - Validation result with isValid boolean and error message
 */
export const validateBloodPressureThresholds = (minThreshold, maxThreshold, isSystolic = true) => {
  const min = parseFloat(minThreshold);
  const max = parseFloat(maxThreshold);
  
  // Check if values are valid numbers
  if (isNaN(min) || isNaN(max)) {
    return {
      isValid: false,
      errorKey: 'invalid_threshold_values',
      errorParams: {}
    };
  }
  
  const limits = isSystolic ? 
    { 
      minLimit: BLOOD_PRESSURE_VALIDATION.MIN_SYSTOLIC, 
      maxLimit: BLOOD_PRESSURE_VALIDATION.MAX_SYSTOLIC 
    } : 
    { 
      minLimit: BLOOD_PRESSURE_VALIDATION.MIN_DIASTOLIC, 
      maxLimit: BLOOD_PRESSURE_VALIDATION.MAX_DIASTOLIC 
    };
  
  // Check minimum threshold is not too low
  if (min < limits.minLimit) {
    return {
      isValid: false,
      errorKey: 'min_threshold_too_low',
      errorParams: { min: limits.minLimit }
    };
  }
  
  // Check maximum threshold is not too high
  if (max > limits.maxLimit) {
    return {
      isValid: false,
      errorKey: 'max_threshold_too_high',
      errorParams: { max: limits.maxLimit }
    };
  }
  
  // Check that max is sufficiently higher than min
  if (max - min < BLOOD_PRESSURE_VALIDATION.MIN_THRESHOLD_DIFFERENCE) {
    return {
      isValid: false,
      errorKey: 'threshold_range_error',
      errorParams: { diff: BLOOD_PRESSURE_VALIDATION.MIN_THRESHOLD_DIFFERENCE }
    };
  }
  
  return {
    isValid: true,
    errorKey: null,
    errorParams: {}
  };
};

/**
 * Get threshold validation limits for UI display
 * @param {boolean} isSystolic - True for systolic, false for diastolic
 * @returns {object} - Validation limits
 */
export const getThresholdValidationLimits = (isSystolic = true) => {
  return isSystolic ? 
    {
      minLimit: BLOOD_PRESSURE_VALIDATION.MIN_SYSTOLIC,
      maxLimit: BLOOD_PRESSURE_VALIDATION.MAX_SYSTOLIC,
      recommended: BLOOD_PRESSURE_VALIDATION.RECOMMENDED.SYSTOLIC
    } : 
    {
      minLimit: BLOOD_PRESSURE_VALIDATION.MIN_DIASTOLIC,
      maxLimit: BLOOD_PRESSURE_VALIDATION.MAX_DIASTOLIC,
      recommended: BLOOD_PRESSURE_VALIDATION.RECOMMENDED.DIASTOLIC
    };
};